import { Prose } from "@liberetech/design-system";
import HeroFull, { imageSizes } from "@liberetech/design-system/HeroFull";
import { nanoid } from "nanoid";
import { GetServerSideProps } from "next";
import React from "react";

import ImageCDN from "components/ImageCDN/ImageCDN";
import SectionFAQs from "components/SectionFAQs/SectionFAQs";
import SectionSpecialRates from "components/SectionSpecialRates/SectionSpecialRates";
import SectionStickyNav from "components/SectionStickyNav/SectionStickyNav";
import { FAQPageJsonLd, LanguageAlternates, Seo } from "components/Seo/Seo";
import fetchCms from "lib/client/cms";
import { ssrFetcher } from "lib/client/graphql";
import serverSideTranslations from "lib/i18n/_pagesRouterCompat/serverSideTranslations";
import { Trans, useTranslation } from "lib/i18n/client";
import { useTranslation as useTranslationServer } from "lib/i18n/server";
import { TrackingContextPage } from "lib/tracking";
import withDirectChannelCommonProps from "lib/withDirectChannelCommonProps";
import SectionWithOverlappedImages from "pagesComponents/_shared/SectionWithOverlappedImages/SectionWithOverlappedImages";
import DestinationSectionAssets from "pagesComponents/destinations/[slug]/index/DestinationSectionAssets/DestinationSectionAssets";
import DestinationSectionTravelGuide from "pagesComponents/destinations/[slug]/index/DestinationSectionTravelGuide/DestinationSectionTravelGuide";
import {
  DestinationIndexDocument,
  DestinationIndexQuery,
  DestinationIndexQueryVariables,
} from "pagesComponents/destinations/[slug]/index/index.generated";
import { DirectChannelCommonProps } from "types";

import styles from "./index.module.css";

const Destination: React.FC<DestinationProps & DirectChannelCommonProps> = ({
  destination,
  brand,
  isViewerInCommunity,
}) => {
  const { featuredImage, title, seo, content, about, travelGuide, posts, faq } =
    destination;
  const { t } = useTranslation("destination");
  const hasSpecialRatesSection = brand.id === "libere";

  return (
    <>
      <Seo
        title={seo.title || t("title", { city: title, brand: brand.name })}
        titleTemplate={seo.title ? "%s" : undefined}
        description={seo.metaDesc}
      />
      <HeroFull
        title={
          <Trans
            i18nKey="destination:title"
            components={[<HeroSubtitle />]}
            values={{ city: title }}
          />
        }
        claim={
          <>
            <svg viewBox="0 0 24 24">
              <path d="M3.75 12h.75c1.701 0 3.62.935 4.5 2.25h3c1.505 0 3 1.495 3 3H8.25" />
              <path d="M14.52 15.75h4.98c2.719 0 3 1.5 3 1.5l-10.74 4.362a3.22 3.22 0 0 1-2.638-.11L3.75 18.774" />
              <path d="M18 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.75 10.5h-3v9.75h3V10.5ZM9 7.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
            </svg>
            <span>
              <Trans
                i18nKey={"common:hero.bestPrice"}
                components={[<strong />]}
              />
            </span>
          </>
        }
        image={
          <ImageCDN
            src={featuredImage?.node.sourceUrl || ""}
            alt={title}
            sizes={imageSizes}
          />
        }
      />
      <SectionStickyNav
        sections={[
          { id: "about", title: t("stickyNav.about") },
          { id: "assets", title: t("stickyNav.assets") },
          { id: "special-rates", title: t("stickyNav.specialRates") },
          { id: "faq", title: t("stickyNav.faq") },
          { id: "travel-guide", title: t("stickyNav.travelGuide") },
        ]}
      >
        <SectionWithOverlappedImages
          id="about"
          label={title}
          title={t("about.title", { city: title })}
          description={
            <Prose>
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </Prose>
          }
          images={{
            above: about.images.above.sourceUrl,
            below: about.images.below.sourceUrl,
          }}
        />

        <DestinationSectionAssets
          title={title}
          destination={destination}
          isViewerInCommunity={isViewerInCommunity}
        />

        {hasSpecialRatesSection && (
          <SectionSpecialRates
            label={title}
            title={t("specialRates.title")}
            description={
              <Trans
                i18nKey="destination:specialRates.description"
                values={{ city: title }}
                components={{ strong: <strong /> }}
              />
            }
            id="special-rates"
          />
        )}
        {faq?.faq && (
          <SectionFAQs
            background={hasSpecialRatesSection ? "contrast" : "default"}
            faqs={faq.faq}
            title={t("faq.title", { city: title })}
            label={title}
          />
        )}
        {(posts.edges.length > 0 || travelGuide.travelGuide) && (
          <DestinationSectionTravelGuide
            title={title}
            destination={destination}
            posts={posts}
            travelGuide={travelGuide}
          />
        )}
      </SectionStickyNav>
      {faq?.faq && (
        <FAQPageJsonLd
          mainEntity={faq.faq.map((faqItem) => ({
            questionName: faqItem.question,
            acceptedAnswerText: faqItem.answer.replace(/<\/?[a-z]*>/g, ""),
          }))}
        />
      )}
    </>
  );
};

const HeroSubtitle: React.FC = ({ children }) => (
  <span className={styles.subtitle}>{children}</span>
);

export const getServerSideProps: GetServerSideProps =
  withDirectChannelCommonProps<DestinationProps>(
    async ({ locale, params, req }) => {
      if (!locale || !params || !params.slug || Array.isArray(params.slug)) {
        return {
          notFound: true,
        };
      }

      const { destination, destinations } = (await fetchCms(
        `{
        destination(id: "/${locale}/${params.slug}", idType: URI) {
          title
          content
          uri
          slug
          about {
            images {
              above {
                sourceUrl
              }
              below {
                sourceUrl
              }
            }
          }
          assets {
            id
            slug
            title
            fullName
            uri
            properties {
              id
              preview
              closed
            }
            featuredImage {
              node {
                srcSet
              }
            }
          }
          faq {
            faq {
              question
              answer
            }
          }
          travelGuide {
            travelGuide {
              image {
                srcSet
              }
              title
              content
            }
          }
          posts(first: 3) {
            edges {
              node {
                title
                uri
                featuredImage {
                  node {
                    srcSet
                  }
                }
              }
            }
          }
          seo {
            title
            metaDesc
          }
          featuredImage {
            node {
              sourceUrl
            }
          }
          translations {
            language {
              slug
            }
            uri
          }
        }
        destinations(where: {language: ${locale.toUpperCase()}, orderby: {field: TITLE, order: ASC}}, first: 100) {
          nodes {
            title
            uri
          }
        }
      }
      `,
        req.context.brand.cmsUrl,
      )) as {
        destination: DestinationResponse;
        destinations: DestinationsResponse;
      };

      if (!destination || destination.title === null) {
        return {
          notFound: true,
        };
      }
      const { loyaltyProgram } = await ssrFetcher<
        DestinationIndexQuery,
        DestinationIndexQueryVariables
      >(req, locale, DestinationIndexDocument, {})();
      const t = await useTranslationServer(locale, "common");

      return {
        props: {
          ...(await serverSideTranslations("/destinations/[slug]", locale)),
          destination,
          isViewerInCommunity: !!loyaltyProgram,
          trackingContext: {
            page_type: "destination",
            attempt_id: nanoid(),
          },
          languageAlternates: [
            {
              hrefLang: locale,
              href: destination.uri,
            },
            ...destination.translations.reduce(
              (prev: LanguageAlternates, translation) => {
                if (!translation.uri || !translation.language.slug) {
                  return prev;
                }
                return [
                  ...prev,
                  {
                    hrefLang: translation.language.slug,
                    href: translation.uri,
                  },
                ];
              },
              [],
            ),
          ],
          breadcrumbs: [
            { name: t("common:breadcrumbs.home"), item: `/${locale}` },
            {
              name: t("common:breadcrumbs.destination", {
                city: destination.title,
              }),
              item: destination.uri,
            },
          ],
          page: {
            searchBar: {
              destinations: destinations.nodes.map((d) => ({
                title: d.title,
                uri: `${d.uri}#assets`,
              })),
              optionsDefault: `${destination.uri}#assets`,
            },
            isRootPage: true,
          },
        },
      };
    },
  );

type DestinationProps = {
  destination: DestinationResponse;
  trackingContext: TrackingContextPage;
  isViewerInCommunity: boolean;
};

type DestinationResponse = {
  title: string;
  content: string;
  uri: string;
  slug: string;
  about: {
    images: {
      above: {
        sourceUrl: string;
      };
      below: {
        sourceUrl: string;
      };
    };
  };
  assets: Array<{
    id: string;
    slug: string;
    title: string;
    fullName: string;
    uri: string;
    properties: {
      id: string;
      preview: string;
      closed: boolean;
    };
    featuredImage: {
      node: {
        srcSet: string;
      };
    };
  }>;
  faq: {
    faq: Array<{
      question: string;
      answer: string;
    }>;
  };
  travelGuide: {
    travelGuide: Array<{
      image: {
        srcSet: string;
      };
      title: string;
      content: string;
    }> | null;
  };
  posts: {
    edges: Array<{
      node: {
        title: string;
        uri: string;
        featuredImage: {
          node: {
            srcSet: string;
          };
        };
      };
    }>;
  };
  featuredImage: {
    node: {
      sourceUrl: string;
    };
  };
  seo: {
    title: string;
    metaDesc: string;
  };
  translations: Array<{
    uri: string;
    language: {
      slug: string;
    };
  }>;
  hero: {
    description?: string;
  };
};

type DestinationsResponse = {
  nodes: Array<{
    title: string;
    uri: string;
  }>;
};

export default Destination;
